import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, CircularProgress, Alert, Autocomplete, Link
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../config/api';
import { useBrand } from '../../context/BrandContext';

/**
 * Export dialog — lets users export a template's results to a new Google Sheet.
 * Props:
 *   open: boolean
 *   onClose: () => void
 */
export default function GoogleSheetsExport({ open, onClose }) {
  const { brands, selectedBrand } = useBrand();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [brand, setBrand] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Load templates on first open
  useEffect(() => {
    if (open && templates.length === 0) {
      setLoadingTemplates(true);
      api.get('/api/mcp-admin/templates')
        .then(res => setTemplates(res.data || []))
        .catch(() => setError('Failed to load templates'))
        .finally(() => setLoadingTemplates(false));
    }
  }, [open, templates.length]);

  // Default brand to selected brand
  useEffect(() => {
    if (open && selectedBrand && !brand) {
      setBrand(selectedBrand);
    }
  }, [open, selectedBrand, brand]);

  const handleExport = async () => {
    if (!selectedTemplate || !brand) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await api.post('/api/google-sheets/export', {
        templateId: selectedTemplate.id,
        brandId: brand.id,
        title: title || `${selectedTemplate.name} - ${brand.name}`,
      });
      if (res.data.success) {
        setResult(res.data);
      } else {
        setError(res.data.error || 'Export failed');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.data?.code === 'NO_GOOGLE_CONNECTION') {
        setError('No Google account connected. Go to Connectors to connect Google Workspace.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError('');
    setSelectedTemplate(null);
    setBrand(null);
    setTitle('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export to Google Sheets</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Export Complete</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {result.rowsWritten?.toLocaleString()} rows exported
              {result.tabCount > 1 ? ` across ${result.tabCount} tabs` : ''}
            </Typography>
            <Link
              href={result.spreadsheetUrl}
              target="_blank"
              rel="noopener"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1 }}
            >
              Open in Google Sheets <OpenInNewIcon fontSize="small" />
            </Link>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <Autocomplete
              options={templates}
              getOptionLabel={(t) => t.name || t.id}
              value={selectedTemplate}
              onChange={(_, v) => setSelectedTemplate(v)}
              loading={loadingTemplates}
              renderInput={(params) => (
                <TextField {...params} label="Report Template" placeholder="Search templates..." />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />

            <Autocomplete
              options={brands || []}
              getOptionLabel={(b) => b.name}
              value={brand}
              onChange={(_, v) => setBrand(v)}
              renderInput={(params) => (
                <TextField {...params} label="Brand" />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />

            <TextField
              label="Sheet Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={selectedTemplate && brand ? `${selectedTemplate.name} - ${brand.name}` : ''}
              size="small"
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
            onClick={handleExport}
            disabled={loading || !selectedTemplate || !brand}
          >
            {loading ? 'Exporting...' : 'Export'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
